/**
 * Picture-word icon lookup (specs/picture-word-mode.md §5).
 *
 * One icon per word, named by the word's language-independent `id`, so the same file serves every
 * language — `pets-dog.webp` is the picture whether the tile reads "dog", "Hund" or "chien".
 *
 * Kept in its own module (rather than inlined in BoardScene) so the path convention is stated once
 * and the data test can assert against the same constant the game loads from.
 */

import type { BoardRule } from '@/game/rules/BoardRule';
import type { PackSet } from '@/data/packs/types';

/** Public directory the icons are served from. Precached — see the spec's §5.1 budget. */
export const WORD_ICON_DIR = '/images/words';

/** Phaser texture key for a word icon. Prefixed so it can never collide with a backdrop key. */
export function wordIconKey(wordId: string): string {
  return `wi_${wordId}`;
}

/** URL the icon is loaded from. */
export function wordIconUrl(wordId: string): string {
  return `${WORD_ICON_DIR}/${wordId}.webp`;
}

/**
 * Every icon a board will need: one per target, since a picture board asks its question with the
 * target's picture and works through them one at a time. Empty for every other kind of board.
 *
 * Two callers have to agree on this list to the key: BoardScene.preload, which is what actually
 * makes the board work, and RoundScene.warmBoardAssets, which loads them one board early so the
 * scene restart finds them cached (see there for what the miss costs). A drift between the two
 * would silently turn the warm-up into a no-op, so the list is derived here, once.
 */
export function boardIconAssets(rule: BoardRule, set: PackSet): { key: string; url: string }[] {
  // A rule with no `iconId` is not a picture board — its banner is text and there is nothing to
  // load. Same check BoardScene.isPictureBoard makes.
  if (typeof rule.iconId !== 'string') return [];
  const assets: { key: string; url: string }[] = [];
  for (const w of set.words) {
    if (!rule.willBeTarget?.(w)) continue;
    const id = typeof w.id === 'string' ? w.id : w.text;
    assets.push({ key: wordIconKey(id), url: wordIconUrl(id) });
  }
  return assets;
}
